import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, LayoutGrid, Map as MapIcon, X, Clock, Moon } from "lucide-react";
import api from "@/lib/api";
import { CATEGORIES, SPORTS_TAGS, FITNESS_TAGS } from "@/lib/constants";
import { PlaceCard } from "@/components/PlaceCard";
import { MapView } from "@/components/MapView";
import { CategoryIcon, CATEGORY_COLORS, SportIcon } from "@/components/CategoryIcon";
import { isOpenNow, isOpenLate } from "@/lib/hours";

export default function Explore() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [places, setPlaces] = useState([]);
  const [view, setView] = useState("grid");
  const [q, setQ] = useState("");
  const [openNow, setOpenNow] = useState(false);
  const [openLate, setOpenLate] = useState(false);
  const [selectedSports, setSelectedSports] = useState([]);
  const toggleSport = (s) =>
    setSelectedSports((p) => (p.includes(s) ? p.filter((x) => x !== s) : [...p, s]));
  const activeCategory = searchParams.get("category") || "";
  const activeTag = searchParams.get("tag") || "";
  const featured = searchParams.get("featured") || "";

  useEffect(() => {
    const params = {};
    if (activeCategory) params.category = activeCategory;
    if (activeTag) params.tag = activeTag;
    if (featured) params.featured = true;
    if (q) params.q = q;
    api.get("/places", { params }).then((r) => setPlaces(r.data));
  }, [activeCategory, activeTag, featured, q]);

  const setCategory = (cat) => {
    const next = new URLSearchParams(searchParams);
    if (cat === activeCategory || cat === "") next.delete("category");
    else next.set("category", cat);
    next.delete("mood");
    setSelectedSports([]);
    setSearchParams(next);
  };

  const clearFilters = () => setSearchParams({});
  const hasFilters = activeCategory || activeTag || featured;

  const shown = useMemo(() => {
    let out = places;
    if (openNow) out = out.filter(isOpenNow);
    if (openLate) out = out.filter(isOpenLate);
    if ((activeCategory === "Sports" || activeCategory === "Fitness") && selectedSports.length)
      out = out.filter((p) => (p.tags || []).some((t) => selectedSports.includes(t)));
    return out;
  }, [places, openNow, openLate, activeCategory, selectedSports]);

  const label = useMemo(() => {
    if (activeCategory) return activeCategory;
    if (activeTag) return activeTag.replace("-", " ");
    if (featured) return "Featured spots";
    return "Everything in the CSRA";
  }, [activeCategory, activeTag, featured]);

  return (
    <div className="px-4 py-6 md:px-8">
      <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Explore</span>
          <h1 className="font-serif text-3xl font-bold capitalize md:text-4xl">{label}</h1>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative flex-1 md:w-64">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search places…"
              data-testid="explore-search"
              className="w-full rounded-full border border-border bg-card py-2.5 pl-9 pr-4 text-sm outline-none focus:border-primary"
            />
          </div>
          <div className="flex rounded-full border border-border bg-card p-1">
            <button onClick={() => setView("grid")} data-testid="view-grid"
              className={`rounded-full p-2 ${view === "grid" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
              <LayoutGrid size={16} />
            </button>
            <button onClick={() => setView("map")} data-testid="view-map"
              className={`rounded-full p-2 ${view === "map" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
              <MapIcon size={16} />
            </button>
          </div>
          <button onClick={() => setOpenNow((v) => !v)} data-testid="explore-open-now"
            aria-pressed={openNow}
            className={`flex items-center gap-1.5 rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${openNow ? "border-green-500 bg-green-500 text-white" : "border-border bg-card text-foreground hover:border-primary"}`}>
            <Clock size={14} /> Open now
          </button>
          <button onClick={() => setOpenLate((v) => !v)} data-testid="explore-open-late"
            aria-pressed={openLate}
            className={`flex items-center gap-1.5 rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${openLate ? "border-indigo-500 bg-indigo-500 text-white" : "border-border bg-card text-foreground hover:border-primary"}`}>
            <Moon size={14} /> Open past 9 PM
          </button>
        </div>
      </div>

      {/* Category pills */}
      <div className="no-scrollbar mb-6 flex gap-2 overflow-x-auto pb-2 md:flex-wrap md:overflow-visible">
        <button onClick={() => setCategory("")} data-testid="category-all"
          className={`flex-shrink-0 rounded-full px-4 py-2 text-sm font-medium transition-colors ${!activeCategory ? "bg-foreground text-background" : "border border-border bg-card"}`}>
          All
        </button>
        {CATEGORIES.map((cat) => (
          <button key={cat} onClick={() => setCategory(cat)} data-testid={`category-${cat}`}
            className={`flex flex-shrink-0 items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium transition-colors ${activeCategory === cat ? "text-white" : "border border-border bg-card text-foreground"}`}
            style={activeCategory === cat ? { backgroundColor: CATEGORY_COLORS[cat] } : {}}>
            <CategoryIcon category={cat} className="h-3.5 w-3.5" /> {cat}
          </button>
        ))}
        {hasFilters && (
          <button onClick={clearFilters} data-testid="clear-filters"
            className="flex flex-shrink-0 items-center gap-1 rounded-full border border-border bg-card px-3 py-2 text-sm text-muted-foreground">
            <X size={14} /> Clear
          </button>
        )}
      </div>

      {/* Secondary activity chips — when Sports or Fitness category is active */}
      {(activeCategory === "Sports" || activeCategory === "Fitness") && (
        <div className="no-scrollbar -mt-2 mb-6 flex items-center gap-2 overflow-x-auto pb-2"
          data-testid={activeCategory === "Sports" ? "sports-subfilter" : "fitness-subfilter"}>
          <span className="flex-shrink-0 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{activeCategory}:</span>
          {(activeCategory === "Sports" ? SPORTS_TAGS : FITNESS_TAGS).map((s) => {
            const on = selectedSports.includes(s);
            return (
              <button key={s} onClick={() => toggleSport(s)} data-testid={`sport-${s.toLowerCase().replace(/\s+/g, "-")}`}
                aria-pressed={on}
                className={`flex flex-shrink-0 items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium transition-colors ${on ? "text-white" : "border border-border bg-card text-foreground hover:border-primary"}`}
                style={on ? { backgroundColor: CATEGORY_COLORS[activeCategory] } : {}}>
                <SportIcon sport={s} className="h-3.5 w-3.5" /> {s}
              </button>
            );
          })}
          {selectedSports.length > 0 && (
            <button onClick={() => setSelectedSports([])} data-testid="clear-sports"
              className="flex flex-shrink-0 items-center gap-1 rounded-full border border-border bg-card px-3 py-2 text-sm text-muted-foreground">
              <X size={14} /> Clear
            </button>
          )}
        </div>
      )}

      {view === "map" ? (
        <MapView places={shown} />
      ) : shown.length ? (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {shown.map((p, i) => <PlaceCard key={p.id} place={p} index={i} />)}
        </div>
      ) : (
        <div className="py-20 text-center text-muted-foreground" data-testid="no-results">
          {openNow ? "Nothing's open right now — try turning off the Open now filter." : "No places match your filters yet."}
        </div>
      )}
    </div>
  );
}
